import { Box } from '@mui/material';
import { useWidgetContext, useWidgetExpandCollapse } from '@ptek/widget-core';
import { ComponentType, useEffect, useRef } from 'react';
import { useWidgetAuth } from '../../../context/AuthContext';
import { AmbientScribeContext } from '../../../types';
import { isStandaloneMode, useStandaloneAutoLogin } from '../../../utils';
import { useHostAuthStatus } from '../hooks/useHostAuthStatus';
import { authGuardStyles as styles } from '../styles/authGuardStyles';

import { LoginForm } from './LoginForm';
import { NewPasswordForm } from './NewPasswordForm';
import { NoAccessPage } from './NoAccessPage';

interface AuthGuardProps {
  children: React.ReactNode;
}

/**
 * Authentication guard component
 * Shows compact login form when authentication is required
 * Shows new password form when NEW_PASSWORD_REQUIRED challenge is active
 * Auto-expands widget when login is needed for better UX
 */
export const AuthGuard: ComponentType<AuthGuardProps> = ({ children }) => {
  const { isAuthenticated, requireAuth, isLoading, challenge, login } =
    useWidgetAuth();
  const { expand } = useWidgetExpandCollapse();
  const { shouldShowNoAccess, shouldShowLoading } = useHostAuthStatus();
  const { widgetContext } = useWidgetContext<AmbientScribeContext>();
  const autoLoginAttempted = useRef(false);
  const autoLoginFailed = useRef(false);

  const autoLoginUtil = useStandaloneAutoLogin({
    context: widgetContext.context,
    requireAuth,
    isAuthenticated,
    isLoading,
    login,
    autoLoginAttempted,
    autoLoginFailed,
  });

  useEffect(() => {
    if (autoLoginUtil.shouldAutoLogin) {
      autoLoginUtil.triggerAutoLogin();
    }

    return () => {
      autoLoginUtil.cleanup();
    };
  }, [
    requireAuth,
    isAuthenticated,
    isLoading,
    widgetContext.context.standalone,
    login,
    autoLoginUtil,
  ]);

  useEffect(() => {
    autoLoginUtil.resetAutoLoginState();
  }, [widgetContext.context.standalone, autoLoginUtil]);

  useEffect(() => {
    if (
      requireAuth &&
      !isAuthenticated &&
      !isLoading &&
      !isStandaloneMode(widgetContext.context)
    ) {
      expand();
    }
  }, [requireAuth, isAuthenticated, isLoading, expand, widgetContext.context]);

  if (!requireAuth) {
    return <>{children}</>;
  }

  if (isAuthenticated) {
    return <>{children}</>;
  }

  if (isLoading || shouldShowLoading) {
    return (
      <Box sx={styles.loadingContainer}>
        <Box sx={styles.loadingText}>Authenticating...</Box>
      </Box>
    );
  }

  // This will show the authenticating screen if auto-login is configured and hasn't failed instead of normal login form
  const hasAutoLogin =
    autoLoginUtil.isStandalone && autoLoginUtil.autoLoginConfig;

  if (hasAutoLogin && !isAuthenticated && !autoLoginFailed.current) {
    return (
      <Box sx={styles.loadingContainer}>
        <Box sx={styles.loadingText}>Authenticating...</Box>
      </Box>
    );
  }

  if (shouldShowNoAccess) {
    return <NoAccessPage />;
  }

  return (
    <Box sx={styles.authContainer}>
      {challenge && challenge.type === 'NEW_PASSWORD_REQUIRED' ? (
        <NewPasswordForm username={challenge.username} />
      ) : (
        <LoginForm />
      )}
    </Box>
  );
};
